import * as React from 'react';
import {SiteSearchConfig} from '../../empracoTypes';
import {FaIcon} from '../../generic/icon';
import {GroupedResponse, SearchType} from './site-search-request';
import {ResultItem} from './site-search-result-items';
import {useCurrentSiteSearchContext} from './site-search-common';

interface NoResultProps {
  searching: boolean;
}

export function NoResult({ searching }: NoResultProps) {
  const { config } = useCurrentSiteSearchContext();

  return <div className='container element-flex no-search-result'>
    {searching
      ? <FaIcon clsName='fa-regular fa-circle-notch fa-spin' />
      : <>
        <FaIcon clsName='fas fa-box-open' />
        {config.primeflow?.translations['SearchResultsNoResults'] ?? config.noResultFoundText}
      </>}
  </div>;
}

function getResultGroupTitle(group: GroupedResponse, config: SiteSearchConfig) {
  const getContent = () => {
    if (group.type === SearchType.umbracoContent) {
      return {
        title: config.primeflow?.translations?.['SearchResultsPagesAndCategories'] || 'Sidor och kategorier',
        icon: <FaIcon clsName='fa-regular fa-folder-tree' />
      };
    }

    if (group.type === SearchType.product) {
      return {
        title: config.primeflow?.translations?.['SearchResultsProducts'] || 'Produkter',
        icon: <FaIcon clsName='fa-regular fa-shopping-cart' />
      };
    }

    return {
      title: config.primeflow?.translations?.['SearchResultsVehicles'] || 'Fordon',
      icon: <FaIcon clsName='fa-regular fa-car-bus' />
    };
  };

  const content = getContent();

  return <p className='empori-site-group-title element-flex'>
    <strong className='element-flex'>
      {content.icon}
      {content.title}
    </strong>

    <small>
      {group.totalCount} {config.primeflow?.translations['SearchResults']?.toLowerCase() ?? 'sökresultat'}
    </small>
  </p>;
}

interface SearchResultGroupProps {
  group: GroupedResponse;
}

function SearchResultGroup({ group }: SearchResultGroupProps) {
  const { config } = useCurrentSiteSearchContext();

  const getLink = () => {
    if (group.type === SearchType.product && config.primeflow.resultsPageUrl) {
      return `${config.primeflow.resultsPageUrl}/search?q=${group.searchTerm}&mode=text`;
    }

    if (group.type === SearchType.vehicle && config.onlineShowroom.resultsPageUrl) {
      return config.onlineShowroom.resultsPageUrl;
    }

    return '';
  };

  const link = getLink();

  if (group.results == null || group.results.length === 0) {
    return null;
  }

  return <div className={`empori-site-search-result-group type-${SearchType[group.type]}`}>
    {getResultGroupTitle(group, config)}

    <div className='empori-site-search-result-group-items'>
      {group.results.map((item, id) =>
        <ResultItem
          type={group.type}
          key={id}
          item={item}
        />
      )}

      {link &&
        <a className='empori-site-search-result-item see-all-link element-flex' href={link}>
          {config.primeflow?.translations['SearchResultShowAll'] ?? 'Se alla'}
          {group.type === SearchType.product && ` (${group.totalCount})`}
          <FaIcon clsName='fa-regular fa-arrow-right' />
        </a>}
    </div>
  </div>;
}

export function SearchResultsList() {
  const { response } = useCurrentSiteSearchContext();

  return <div className='container element-flex'>
    {response.map((group, index) =>
      <SearchResultGroup
        key={index}
        group={group}
      />
    )}
  </div>;
}